import { useState } from "react";
import { siteInfo } from "@/mocks/siteContent";
import SectionHeader from "./SectionHeader";

const FORM_URL = "https://readdy.ai/api/form/da2lbqjd3r93mn1k3bcg";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 反垃圾蜜罐字段：非空则静默当作成功处理
    const honey = (formData.get("company_alt") as string) || "";
    if (honey.trim() !== "") {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("company_alt");

    setStatus("loading");
    setFormError("");

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const ok = res.ok && parsed?.code === "OK" && !/spam/i.test(String(serverMsg));

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <section id="contact" className="w-full bg-background-100/60 py-14 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <SectionHeader
              eyebrow="联系我们"
              title="有想看的漫画，或想说的建议？"
              desc="无论是作品推荐、版权合作，还是使用过程中遇到的任何问题，都欢迎通过表单或邮箱与我们联系，我们会尽快回复。"
            />
            <div className="space-y-4">
              <a href={`mailto:${siteInfo.email}`} className="flex items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-600">
                  <i className="ri-mail-line" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">商务合作 / 版权</span>
                  <span className="block text-sm font-semibold text-foreground-900">{siteInfo.email}</span>
                </span>
              </a>
              <a href="#hot" className="flex items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-xl text-accent-700">
                  <i className="ri-feedback-line" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">反馈建议</span>
                  <span className="block text-sm font-semibold text-foreground-900">关于更新与排行的建议，欢迎告诉我们</span>
                </span>
              </a>
              <div className="rounded-xl border border-background-200 bg-background-50 p-4">
                <span className="block text-xs text-foreground-500">营业时间（在线反馈）</span>
                <span className="mt-1 flex items-center gap-2 text-sm font-semibold text-foreground-900">
                  <i className="ri-time-line text-accent-600" aria-hidden="true" />
                  周一至周日 9:00 - 22:00
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-background-200 bg-background-50 p-6">
            <h3 className="font-heading text-xl text-foreground-950">给我们留言</h3>
            <p className="mt-1 text-xs text-foreground-500">带 * 号为必填项，我们承诺保护你的隐私</p>

            <form data-readdy-form="da2lbqjd3r93mn1k3bcg" onSubmit={handleSubmit} className="mt-5 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">您的称呼 *</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼您"
                    className="h-11 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">联系邮箱 *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    className="h-11 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">主题 *</label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  placeholder="如：作品推荐、版权合作、使用反馈…"
                  className="h-11 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                />
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">留言内容 *</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请尽量详细描述你的需求或建议（最多500字）"
                  className="w-full resize-none rounded-md border border-background-200 bg-background-100 px-3 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                />
              </div>

              {/* 反垃圾蜜罐字段 */}
              <div className="hp-aux-guard" aria-hidden="true">
                <label htmlFor="company_alt">请不要填写此字段</label>
                <input
                  id="company_alt"
                  name="company_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              {status === "error" && (
                <div className="rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-700" role="alert">
                  <i className="ri-error-warning-fill mr-1" aria-hidden="true" />
                  {formError || "提交失败，请稍后再试。"}
                </div>
              )}
              {status === "success" && (
                <div className="rounded-md bg-accent-50 px-4 py-3 text-sm text-accent-800" role="status">
                  <i className="ri-checkbox-circle-fill mr-1" aria-hidden="true" />
                  提交成功！感谢你的留言，我们会尽快与你联系。
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill" aria-hidden="true" />
                    提交留言
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}